import { useState, useEffect } from 'react'
import { cn } from '@/utils/cn'

interface SpecificInfoData {
  // Produtor rural
  propertySizeHa?: string
  biome?: string
  soilType?: string
  propertyCity?: string
  propertyState?: string
  productionSystem?: string
  gaasAssociated?: string
  activities?: string[]
  practices?: string[]
  challenges?: string[]
  mainObjective?: string

  // Técnico/Consultor
  professionalFormation?: string
  workArea?: string
  workRegion?: string
  workType?: string
  regenerativeExperience?: string
  experienceYears?: string
  howUseRegenerai?: string

  // Pesquisador/Estudante/Instituição
  institution?: string
  workField?: string
  mainInterest?: string
  preferredTopics?: string[]
}

interface SpecificInfoStepProps {
  profileType: string
  data?: Partial<SpecificInfoData>
  onChange: (data: Partial<SpecificInfoData>) => void
  onValidationChange: (isValid: boolean) => void
}

const biomes = [
  'Amazônia', 'Cerrado', 'Caatinga', 'Mata Atlântica', 'Pampa', 'Pantanal', 'Outro'
]

const productionSystems = [
  'Convencional', 'Em transição', 'Agroecológico', 'Orgânico', 'Regenerativo', 'Outro'
]

const activities = [
  'Grãos', 'Cana', 'Café', 'Frutas', 'Hortaliças', 'Floresta',
  'Gado de corte', 'Gado de leite', 'Aves de postura', 'Aves de corte', 'Suínos', 'Outro'
]

const practices = [
  'Plantio direto', 'Rotação de culturas', 'Cobertura permanente do solo',
  'Adubação verde', 'Bioinsumos', 'Compostagem', 'Integração lavoura-pecuária',
  'Integração lavoura-pecuária-floresta', 'Pastejo rotacionado', 'Irrigação', 'Outro'
]

const challenges = [
  'Solo', 'Água', 'Pragas', 'Doenças', 'Plantas daninhas', 'Clima',
  'Custo de produção', 'Mão de obra', 'Mercado', 'Outro'
]

const workRegions = [
  'Norte', 'Nordeste', 'Centro-Oeste', 'Sudeste', 'Sul', 'Nacional'
]

const workTypes = [
  'Autônomo(a)', 'Empresa', 'Cooperativa', 'Assistência técnica',
  'Instituição pública', 'Pesquisa', 'Ensino', 'Outro'
]

const workFields = [
  'Empresa', 'Instituição de ensino', 'Instituição de pesquisa',
  'Poder público', 'Terceiro setor', 'Outro'
]

const preferredTopics = [
  'Solo', 'Clima', 'Bioinsumos', 'Nutrição de plantas', 'Manejo de pragas',
  'Pecuária', 'Transição regenerativa', 'Planejamento produtivo', 'Mercado', 'Outro'
]

export function SpecificInfoStep({ profileType, data, onChange, onValidationChange }: SpecificInfoStepProps) {
  const [selectedActivities, setSelectedActivities] = useState<string[]>(data?.activities || [])
  const [selectedPractices, setSelectedPractices] = useState<string[]>(data?.practices || [])
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>(data?.challenges || [])
  const [selectedTopics, setSelectedTopics] = useState<string[]>(data?.preferredTopics || [])

  // Sempre válido (campos opcionais)
  useEffect(() => {
    onValidationChange(true)
  }, [])

  const handleArrayChange = (field: keyof SpecificInfoData, items: string[]) => {
    onChange({ [field]: items })
  }

  const renderProducerFields = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-brand-text mb-4">Perfil da Propriedade</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Tamanho da área (hectares)
          </label>
          <input
            type="number"
            value={data?.propertySizeHa || ''}
            onChange={(e) => onChange({ propertySizeHa: e.target.value })}
            className="input-field"
            placeholder="Ex: 100"
            step="0.01"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Bioma da propriedade
          </label>
          <select
            value={data?.biome || ''}
            onChange={(e) => onChange({ biome: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {biomes.map(biome => (
              <option key={biome} value={biome}>{biome}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Tipo de solo predominante
          </label>
          <input
            type="text"
            value={data?.soilType || ''}
            onChange={(e) => onChange({ soilType: e.target.value })}
            className="input-field"
            placeholder="Ex: Latossolo vermelho"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Sistema de produção atual
          </label>
          <select
            value={data?.productionSystem || ''}
            onChange={(e) => onChange({ productionSystem: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {productionSystems.map(system => (
              <option key={system} value={system}>{system}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            É associado ao GAAS?
          </label>
          <select
            value={data?.gaasAssociated || ''}
            onChange={(e) => onChange({ gaasAssociated: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            <option value="yes">Sim</option>
            <option value="no">Não</option>
            <option value="dont_know">Ainda não sei</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Principal objetivo ao usar o RegenerAI
          </label>
          <select
            value={data?.mainObjective || ''}
            onChange={(e) => onChange({ mainObjective: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            <option value="doubts">Tirar dúvidas</option>
            <option value="recommendations">Receber recomendações</option>
            <option value="compare">Comparar com casos semelhantes</option>
            <option value="register">Registrar experiências</option>
            <option value="follow">Acompanhar a transição produtiva</option>
            <option value="learn">Aprender com ciência aplicada</option>
            <option value="other">Outro</option>
          </select>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-brand-text mb-4 mt-8">Atividade Produtiva</h3>

      <div>
        <label className="block text-sm font-medium text-brand-text mb-3">
          O que cultiva ou cria? (Marque uma ou mais opções)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {activities.map(activity => (
            <button
              key={activity}
              type="button"
              onClick={() => {
                const newActivities = selectedActivities.includes(activity)
                  ? selectedActivities.filter(a => a !== activity)
                  : [...selectedActivities, activity]
                setSelectedActivities(newActivities)
                handleArrayChange('activities', newActivities)
              }}
              className={cn(
                "p-3 rounded-lg border text-sm transition-all",
                selectedActivities.includes(activity)
                  ? "border-brand-primary bg-brand-primary/10 shadow-md ring-1 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              {activity}
            </button>
          ))}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-brand-text mb-4 mt-8">Manejo e Contexto Técnico</h3>

      <div>
        <label className="block text-sm font-medium text-brand-text mb-3">
          Quais práticas de manejo utiliza atualmente?
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {practices.map(practice => (
            <button
              key={practice}
              type="button"
              onClick={() => {
                const newPractices = selectedPractices.includes(practice)
                  ? selectedPractices.filter(p => p !== practice)
                  : [...selectedPractices, practice]
                setSelectedPractices(newPractices)
                handleArrayChange('practices', newPractices)
              }}
              className={cn(
                "p-3 rounded-lg border text-sm transition-all",
                selectedPractices.includes(practice)
                  ? "border-brand-primary bg-brand-primary/10 shadow-md ring-1 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              {practice}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-brand-text mb-3">
          Quais são hoje os principais desafios da sua propriedade?
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {challenges.map(challenge => (
            <button
              key={challenge}
              type="button"
              onClick={() => {
                const newChallenges = selectedChallenges.includes(challenge)
                  ? selectedChallenges.filter(c => c !== challenge)
                  : [...selectedChallenges, challenge]
                setSelectedChallenges(newChallenges)
                handleArrayChange('challenges', newChallenges)
              }}
              className={cn(
                "p-3 rounded-lg border text-sm transition-all",
                selectedChallenges.includes(challenge)
                  ? "border-brand-primary bg-brand-primary/10 shadow-md ring-1 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              {challenge}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const renderTechnicianFields = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-brand-text mb-4">Perfil Profissional</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Formação profissional
          </label>
          <input
            type="text"
            value={data?.professionalFormation || ''}
            onChange={(e) => onChange({ professionalFormation: e.target.value })}
            className="input-field"
            placeholder="Ex: Engenheiro Agrônomo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Área de atuação
          </label>
          <input
            type="text"
            value={data?.workArea || ''}
            onChange={(e) => onChange({ workArea: e.target.value })}
            className="input-field"
            placeholder="Ex: Agricultura familiar"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Região de atuação
          </label>
          <select
            value={data?.workRegion || ''}
            onChange={(e) => onChange({ workRegion: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {workRegions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Tipo de vínculo
          </label>
          <select
            value={data?.workType || ''}
            onChange={(e) => onChange({ workType: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {workTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Atua com agricultura regenerativa?
          </label>
          <select
            value={data?.regenerativeExperience || ''}
            onChange={(e) => onChange({ regenerativeExperience: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            <option value="yes">Sim</option>
            <option value="no">Não</option>
            <option value="transition">Em fase de transição</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Há quanto tempo atua na área?
          </label>
          <input
            type="text"
            value={data?.experienceYears || ''}
            onChange={(e) => onChange({ experienceYears: e.target.value })}
            className="input-field"
            placeholder="Ex: 5 anos"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-brand-text mb-2">
            Como pretende usar o RegenerAI?
          </label>
          <select
            value={data?.howUseRegenerai || ''}
            onChange={(e) => onChange({ howUseRegenerai: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            <option value="support">Apoiar produtores</option>
            <option value="references">Buscar referências</option>
            <option value="register">Registrar casos</option>
            <option value="validate">Validar práticas</option>
            <option value="protocols">Construir protocolos</option>
            <option value="other">Outro</option>
          </select>
        </div>
      </div>
    </div>
  )

  const renderResearcherFields = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-brand-text mb-4">Informações Institucionais</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Instituição / Organização
          </label>
          <input
            type="text"
            value={data?.institution || ''}
            onChange={(e) => onChange({ institution: e.target.value })}
            className="input-field"
            placeholder="Nome da instituição"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Profissão / Área de formação
          </label>
          <input
            type="text"
            value={data?.professionalFormation || ''}
            onChange={(e) => onChange({ professionalFormation: e.target.value })}
            className="input-field"
            placeholder="Sua formação"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Tipo de atuação
          </label>
          <select
            value={data?.workField || ''}
            onChange={(e) => onChange({ workField: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            {workFields.map(field => (
              <option key={field} value={field}>{field}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-text mb-2">
            Qual é seu interesse no RegenerAI?
          </label>
          <select
            value={data?.mainInterest || ''}
            onChange={(e) => onChange({ mainInterest: e.target.value })}
            className="input-field"
          >
            <option value="">Selecione...</option>
            <option value="learn">Aprender</option>
            <option value="research">Pesquisar</option>
            <option value="support">Apoiar produtores</option>
            <option value="develop">Desenvolver projetos</option>
            <option value="follow">Acompanhar casos</option>
            <option value="other">Outro</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-brand-text mb-3">
          Temas de maior interesse (Marque uma ou mais opções)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {preferredTopics.map(topic => (
            <button
              key={topic}
              type="button"
              onClick={() => {
                const newTopics = selectedTopics.includes(topic)
                  ? selectedTopics.filter(t => t !== topic)
                  : [...selectedTopics, topic]
                setSelectedTopics(newTopics)
                handleArrayChange('preferredTopics', newTopics)
              }}
              className={cn(
                "p-3 rounded-lg border text-sm transition-all",
                selectedTopics.includes(topic)
                  ? "border-brand-primary bg-brand-primary/10 shadow-md ring-1 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              {topic}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-brand-text-secondary">
          Informações específicas para seu perfil
        </p>
      </div>

      {profileType === 'producer' && renderProducerFields()}
      {profileType === 'technician' && renderTechnicianFields()}
      {(profileType === 'researcher' || profileType === 'student' || profileType === 'institution') && renderResearcherFields()}
      {profileType === 'other' && (
        <div className="text-center py-8">
          <p className="text-brand-text-secondary">
            Para o perfil "Outro", você pode preencher as informações nos próximos passos
            ou adicionar observações específicas no campo final.
          </p>
        </div>
      )}
    </div>
  )
}
